
    import {Inject, Options, Vue} from 'vue-property-decorator';
    import {Tag} from '@/profile-creation-wizard/components/for-creation/Tag';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import {FreelancerTagsService} from '@/profile-creation-wizard/components/for-creation/FreelancerTagsService';
    import SelectedTags from '@/profile-creation-wizard/components/for-creation/ui/SelectedTags.vue';
    import InputSelectTags from '../../InputSelectTags.vue';
    import TagCounterIndicator from '@/profile-creation-wizard/components/for-creation/ui/TagCounterIndicator.vue';
    import {pushNotification} from '@malt/joy-entrypoint/src/js/notification';
    import TagSuggestion from '@/profile-creation-wizard/components/for-creation/ui/TagSuggestion.vue';
    import {Getter} from 'vuex-class';
    import getters from '@/profile-creation-wizard/components/for-creation/store/getters';
    import type {ProfileCreationForm} from '@/profile-creation-wizard/components/for-creation/store';
    import mutations from '@/profile-creation-wizard/components/for-creation/store/mutations';
    import BottomSheetDisplayer from '@/common/ui/BottomSheetDisplayer';
    import {differenceWith, isEqual} from 'lodash';

    @Options({
        components: {
            SelectedTags,
            InputSelectTags,
            TagCounterIndicator,
            TagSuggestion,
        },
    })
    export default class TagsSelection extends Vue {
        @Inject({from: DI.freelancerTagsService})
        freelancerTagsService!: FreelancerTagsService;
        @Getter(getters.creationForm)
        creationForm!: ProfileCreationForm;
        @Getter(getters.selectedCategories)
        selectedCategories!: Array<string>;

        MAX_SELECTED_TAGS_COUNT = 50;
        MAX_SUGGESTED_TAGS_COUNT = 7;

        bottomSheetSelector = '#details-skills-bottom-sheet';
        dataHeapTooltipID = 'profile-creation-tooltip-skills';

        suggesteddTags: Tag[] = [];

        async created(): Promise<void> {
            if (this.mainCategory) {
                const [data, error] = await this.freelancerTagsService.retrieveSuggestions(this.mainCategory);
                if (data && !error) {
                    this.suggesteddTags = data.map((suggestedTag) => new Tag(suggestedTag.tag));
                }
            }
        }

        get tagsDifferrenceWith(): Tag[] {
            return differenceWith(this.suggesteddTags, this.creationForm.tags, isEqual);
        }

        get isNotReachedLimit(): boolean {
            return this.creationForm.tags.length < this.MAX_SELECTED_TAGS_COUNT;
        }

        handlerAddTag(tag: Tag[]): void {
            if (this.isNotReachedLimit) {
                this.$store.commit(mutations.selectTag, tag);
            }
        }

        handlerRemoveTag(tag: Tag[]): void {
            this.$store.commit(mutations.unselectTag, tag);
        }

        get mainCategory(): string | null {
            if (!this.selectedCategories || !this.selectedCategories[0]) {
                return null;
            }
            return this.selectedCategories[0];
        }

        mounted(): void {
            this.bottomSheetDisplayer = new BottomSheetDisplayer(
                this.bottomSheetSelector,
                this.dataHeapTooltipID,
                this.$el,
                'div.profile-creation-wizard',
            );
            this.bottomSheetDisplayer.mounted();
        }

        openBottomSheet(): void {
            this.bottomSheetDisplayer.openBottomSheet();
        }

        selectSearch(tag: Tag): void {
            if (!tag || !tag.name || tag.name.length == 0) {
                return;
            }
            this.pushTag(tag);
        }

        pushTag(tag: Tag): void {
            if (!this.isNotReachedLimit) {
                pushNotification(this.$msg('profile.creation.completion.wizard.details.skills.error.max'), {level: 'error'});
                return;
            }
            if (this.creationForm.tags.some((s: Tag) => this.sanitize(s.name) === this.sanitize(tag.name))) {
                pushNotification(this.$msg('profile.creation.completion.wizard.details.skills.error.duplicate'), {level: 'error'});
                return;
            }
            this.$store.commit(mutations.selectTag, tag);
        }

        sanitize(value: string): string {
            if (!value) {
                return '';
            }

            return value
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase();
        }
    }
